import {
  PublicClientApplication,
  type Configuration,
  LogLevel,
} from '@azure/msal-browser'

// Pool-scoped admin console -- a SECOND, independent MSAL instance, never
// the tenant-facing one from ./msal.ts. Mirrors withAdminAuth.ts on the API
// side exactly: fixed authority (Raven's own Entra tenant, not
// /organizations), a separate app registration, no relationship to any
// pooled tenant's own client ID. See withAdminAuth.ts's own header comment
// for why this is disjoint rather than a mode of the tenant-facing app.
//
// Raven's own Entra tenant -- the same fixed value withAdminAuth.ts checks
// server-side. Hardcoded for the same reason it's hardcoded there: this is
// not configuration that varies per deployment or pool, and there is no
// tenant registry on the frontend to derive it from anyway.
const ADMIN_ENTRA_TENANT_ID = '91401762-c44f-4a65-bedf-63d18cd32444'

// Unlike msal.ts's REQUIRED_BUILD_TIME_VARS (which throw at module load,
// failing the ENTIRE app if missing): this is optional. The admin console
// is a hidden, rarely-visited route -- a build made before Platform Ops has
// created the admin app registration (or before this secret is wired into
// the deploy workflow) must not break the tenant-facing app for every
// other user. isAdminConsoleConfigured() lets the admin route itself
// render a clear "not configured" state instead.
const ADMIN_CLIENT_ID = import.meta.env.VITE_ADMIN_ENTRA_CLIENT_ID as string | undefined

export function isAdminConsoleConfigured(): boolean {
  return Boolean(ADMIN_CLIENT_ID)
}

const adminMsalConfig: Configuration = {
  auth: {
    clientId: ADMIN_CLIENT_ID ?? '',
    authority: `https://login.microsoftonline.com/${ADMIN_ENTRA_TENANT_ID}`,
    // Reuses the same physical redirect URI as the tenant-facing app
    // (same origin, same deployed SWA) -- the admin app registration's own
    // manifest must list this exact URI as an allowed SPA redirect URI too.
    redirectUri: import.meta.env.VITE_ENTRA_REDIRECT_URI,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // Session-scoped, not localStorage: unlike the tenant-facing app (where
    // staying signed in across tabs/restarts is the desired UX for daily
    // use), there is no reason for an admin session to persist beyond the
    // current tab -- this console is visited rarely and deliberately, and
    // keeping its cache smaller/shorter-lived reduces how long an admin
    // token sits in browser storage for no active reason.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level, message) => {
        if (level === LogLevel.Error) console.error('[admin MSAL]', message)
        else if (import.meta.env.DEV) console.debug('[admin MSAL]', message)
      },
    },
  },
}

export const adminMsalInstance = new PublicClientApplication(adminMsalConfig)

export const adminLoginRequest = {
  scopes: ADMIN_CLIENT_ID ? [`api://${ADMIN_CLIENT_ID}/user_impersonation`] : [],
}
